import { scaleLinear } from '@mui/x-charts-vendor/d3-scale';

/**
 * Interpolates a single point at a specific timestamp between two known points.
 * Handles different types of aviation data appropriately.
 *
 * @param point1 - The first point as an array of values (must include timestamp at index 0)
 * @param point2 - The second point as an array of values (must include timestamp at index 0)
 * @param targetTimestamp - The timestamp for the interpolated point
 * @param columns - Array of column names that define what each index represents
 * @returns Interpolated point as an array of values, or null if timestamp is outside range
 */
export function interpolatePointAtTimestamp(
    point1: number[],
    point2: number[],
    targetTimestamp: number,
    columns: string[]
): number[] | null {
    if (point1.length !== point2.length || point1.length !== columns.length) {
        throw new Error('Points and columns must have the same length');
    }

    // Find timestamp column index (should be 0 according to the data structure)
    const timestampIndex = columns.indexOf('timestamp');
    if (timestampIndex === -1) {
        throw new Error('Timestamp column not found in columns array');
    }

    const timestamp1 = point1[timestampIndex];
    const timestamp2 = point2[timestampIndex];

    // Check if target timestamp is within the range
    if (
        targetTimestamp < Math.min(timestamp1, timestamp2) ||
        targetTimestamp > Math.max(timestamp1, timestamp2)
    ) {
        return null;
    }

    // If target timestamp matches one of the endpoints exactly, return that point
    if (targetTimestamp === timestamp1) {
        return [...point1];
    }
    if (targetTimestamp === timestamp2) {
        return [...point2];
    }

    const interpolatedPoint: number[] = [];

    for (let i = 0; i < columns.length; i++) {
        const columnName = columns[i];
        const value1 = point1[i];
        const value2 = point2[i];

        let interpolatedValue: number;

        if (i === timestampIndex) {
            // Use the exact target timestamp
            interpolatedValue = targetTimestamp;
        } else if (isAngularField(columnName)) {
            // Handle angular data (degrees) - need to account for wraparound
            const t =
                (targetTimestamp - timestamp1) / (timestamp2 - timestamp1);
            interpolatedValue = interpolateAngle(value1, value2, t);
        } else {
            // Use D3 linear scale for linear interpolation
            const linearScale = scaleLinear()
                .domain([timestamp1, timestamp2])
                .range([value1, value2]);
            interpolatedValue = linearScale(targetTimestamp);
        }

        interpolatedPoint.push(interpolatedValue);
    }

    return interpolatedPoint;
}

/**
 * Interpolates points between two known points based on the specified resolution.
 * Handles different types of aviation data appropriately.
 *
 * @param point1 - The first point as an array of values
 * @param point2 - The second point as an array of values
 * @param columns - Array of column names that define what each index represents
 * @param resolution - The number of interpolated points to generate between the two points
 * @returns Array of interpolated points, not including the original points
 */
export function interpolatePoints(
    point1: number[],
    point2: number[],
    columns: string[],
    resolution: number
): number[][] {
    if (resolution <= 0) {
        return [];
    }

    if (point1.length !== point2.length || point1.length !== columns.length) {
        throw new Error('Points and columns must have the same length');
    }

    const interpolatedPoints: number[][] = [];
    const steps = resolution + 1; // +1 to exclude the original points

    for (let i = 1; i <= resolution; i++) {
        const t = i / steps; // interpolation factor between 0 and 1
        const interpolatedPoint: number[] = [];

        for (let j = 0; j < columns.length; j++) {
            const columnName = columns[j];
            const value1 = point1[j];
            const value2 = point2[j];

            let interpolatedValue: number;

            // Handle different types of data based on column name
            if (isAngularField(columnName)) {
                // Handle angular data (degrees) - need to account for wraparound
                interpolatedValue = interpolateAngle(value1, value2, t);
            } else if (isTimestamp(columnName)) {
                // Linear interpolation for timestamps
                interpolatedValue = Math.round(value1 + t * (value2 - value1));
            } else {
                // Linear interpolation for all other numeric fields
                interpolatedValue = value1 + t * (value2 - value1);
            }

            interpolatedPoint.push(interpolatedValue);
        }

        interpolatedPoints.push(interpolatedPoint);
    }

    return interpolatedPoints;
}

/**
 * Determines if a column represents angular data that needs special interpolation
 */
function isAngularField(columnName: string): boolean {
    const angularFields = [
        'bankDeg',
        'pitchDeg',
        'magneticCourse',
        'magneticHeading',
        'windDirection',
        'turnRate',
        'magneticVariance',
    ];
    return angularFields.includes(columnName);
}

/**
 * Determines if a column represents a timestamp
 */
function isTimestamp(columnName: string): boolean {
    return columnName === 'timestamp';
}

/**
 * Interpolates between two angles, handling wraparound at 0/360 degrees
 */
function interpolateAngle(angle1: number, angle2: number, t: number): number {
    // Normalize angles to 0-360 range
    const normalizeAngle = (angle: number) => ((angle % 360) + 360) % 360;

    const a1 = normalizeAngle(angle1);
    const a2 = normalizeAngle(angle2);

    // Calculate the shortest angular distance
    let diff = a2 - a1;
    if (diff > 180) {
        diff -= 360;
    } else if (diff < -180) {
        diff += 360;
    }

    // Interpolate and normalize result
    const result = a1 + t * diff;
    return normalizeAngle(result);
}
